import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getWines, getWinesCount, getGrapeVarieties } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { WineScore } from '../components/StarRating';
import { Pagination } from '../components/Pagination';
import { toast } from 'sonner';
import { Wine, Search, Plus, Filter, X, Grape, GitCompare } from 'lucide-react';

const DEFAULT_PAGE_SIZE = 12;
const MAX_COMPARE = 4;

const Wines = () => {
  const { isAuthenticated, canAddContent, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [wines, setWines] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [appending, setAppending] = useState(false);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [grapeVariety, setGrapeVariety] = useState('');
  const [grapeVarieties, setGrapeVarieties] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    (async () => {
      try {
        setGrapeVarieties(await getGrapeVarieties());
      } catch (e) {
        console.error('Failed to load filters:', e);
      }
    })();
  }, []);

  const buildParams = useCallback(() => {
    const params = {};
    if (activeSearch) params.search = activeSearch;
    if (grapeVariety && grapeVariety !== 'all') params.grape_variety = grapeVariety;
    return params;
  }, [activeSearch, grapeVariety]);

  const loadPage = useCallback(async (targetPage, size, append = false, appendSkipOverride = null) => {
    if (append) setAppending(true);
    else setLoading(true);
    try {
      const params = {
        ...buildParams(),
        skip: appendSkipOverride != null ? appendSkipOverride : (targetPage - 1) * size,
        limit: size,
      };
      const [data, count] = await Promise.all([
        getWines(params),
        getWinesCount(buildParams()),
      ]);
      setWines((prev) => (append ? [...prev, ...data] : data));
      setTotalCount(count);
    } catch (e) {
      console.error('Failed to load wines:', e);
    } finally {
      setLoading(false);
      setAppending(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadPage(page, pageSize, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, activeSearch, grapeVariety]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(search);
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setGrapeVariety('');
    setPage(1);
  };

  const handleLoadMore = () => {
    // Append next slice WITHOUT changing `page` (which drives numbered pagination)
    const skipOverride = (page - 1) * pageSize + wines.length;
    loadPage(page, pageSize, true, skipOverride);
  };

  const toggleCompare = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARE) {
        toast.error(`You can compare up to ${MAX_COMPARE} wines at a time`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const goToCompare = () => {
    if (selectedIds.length < 2) {
      toast.error('Select at least 2 wines to compare');
      return;
    }
    navigate(`/compare?ids=${selectedIds.join(',')}`);
  };

  const clearCompare = () => setSelectedIds([]);
  const hasMore = (page - 1) * pageSize + wines.length < totalCount;

  return (
    <div className="min-h-screen pt-8 pb-32" data-testid="wines-page">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2">Discover</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h1 className="text-4xl md:text-5xl font-serif text-stone-50">Wines</h1>
            {isAuthenticated && canAddContent && (
              <Button
                onClick={() => navigate('/wines/new')}
                className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs px-6"
                data-testid="add-wine-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Wine
              </Button>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
              <Input
                type="text"
                placeholder="Search wines, grape varieties, vineyards..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 bg-stone-900/50 border-stone-700 rounded-none h-12"
                data-testid="search-input"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-stone-700 rounded-none h-12"
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button
              type="submit"
              className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none h-12 px-8"
              data-testid="search-submit-btn"
            >
              Search
            </Button>
          </form>

          {showFilters && (
            <div className="mt-4 p-6 bg-stone-900/50 border border-stone-700" data-testid="filter-panel">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">
                    Grape Variety
                  </label>
                  <Select value={grapeVariety} onValueChange={(v) => { setGrapeVariety(v); setPage(1); }}>
                    <SelectTrigger className="bg-stone-900 border-stone-700 rounded-none" data-testid="grape-variety-select">
                      <SelectValue placeholder="All Varieties" />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-900 border-stone-700">
                      <SelectItem value="all">All Varieties</SelectItem>
                      {grapeVarieties.map((v) => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={clearFilters}
                    className="text-stone-500 hover:text-stone-300"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-2" /> Clear
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={`sk-${i}`} className="p-6 bg-stone-900/40 border border-white/5 animate-pulse">
                <div className="flex justify-between mb-4">
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-1/3 bg-stone-800 rounded" />
                    <div className="h-5 w-3/4 bg-stone-800 rounded" />
                  </div>
                  <div className="w-10 h-10 bg-stone-800 rounded-full" />
                </div>
                <div className="h-4 w-1/2 bg-stone-800 rounded" />
              </div>
            ))}
          </div>
        ) : wines.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {wines.map((wine) => {
                const isSelected = selectedIds.includes(wine.id);
                return (
                  <div key={wine.id} className="relative group">
                    <Link
                      to={`/wines/${wine.id}`}
                      className={`wine-card block p-6 bg-stone-900/20 hover:bg-stone-900/60 border transition-all duration-300 ${
                        isSelected ? 'border-[#d4af37]' : 'border-white/5'
                      }`}
                      data-testid={`wine-card-${wine.id}`}
                    >
                      {wine.status && wine.status !== 'approved' && (
                        <span
                          className={`absolute top-3 left-3 z-10 px-2 py-1 text-[10px] uppercase tracking-widest border ${
                            wine.status === 'pending'
                              ? 'bg-[#d4af37]/20 border-[#d4af37]/50 text-[#d4af37]'
                              : 'bg-red-900/60 border-red-500/50 text-red-300'
                          }`}
                          data-testid={`wine-status-${wine.id}`}
                        >
                          {wine.status === 'pending' ? 'Pending' : 'Rejected'}
                        </span>
                      )}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1 pr-8">
                          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#d4af37] mb-2">
                            <Grape className="w-3 h-3" />
                            {wine.grape_variety}
                          </div>
                          <h3 className="text-lg font-serif text-stone-50 group-hover:text-[#d4af37] transition-colors line-clamp-2">
                            {wine.name}
                          </h3>
                        </div>
                        {wine.average_rating > 0 && (
                          <WineScore score={wine.average_rating} size="sm" />
                        )}
                      </div>
                      <p className="text-sm text-stone-500 mb-1">{wine.vineyard_name}</p>
                      {wine.vintage && (
                        <p className="text-xs text-stone-600">Vintage {wine.vintage}</p>
                      )}
                      {wine.rating_count > 0 && (
                        <p className="text-xs text-stone-600 mt-2">
                          {wine.rating_count} {wine.rating_count === 1 ? 'rating' : 'ratings'}
                        </p>
                      )}
                    </Link>

                    {/* Compare checkbox (absolute overlay, stops propagation) */}
                    <label
                      className={`absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2 py-1 border text-[10px] uppercase tracking-widest cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-[#d4af37] text-stone-950 border-[#d4af37]'
                          : 'bg-stone-950/70 text-stone-400 border-stone-700 opacity-0 group-hover:opacity-100 hover:border-[#d4af37] hover:text-[#d4af37]'
                      }`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`compare-toggle-${wine.id}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleCompare(wine.id)}
                        className="h-3 w-3 border-current data-[state=checked]:bg-stone-950 data-[state=checked]:text-[#d4af37]"
                      />
                      Compare
                    </label>
                  </div>
                );
              })}
            </div>

            <Pagination
              currentPage={page}
              totalItems={totalCount}
              pageSize={pageSize}
              loadedCount={wines.length}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
              loading={appending}
            />
          </>
        ) : (
          <div className="text-center py-20">
            <Wine className="w-16 h-16 text-stone-700 mx-auto mb-6" />
            <h3 className="text-2xl font-serif text-stone-400 mb-2">No Wines Found</h3>
            <p className="text-stone-600 mb-6">
              {activeSearch || grapeVariety
                ? 'Try adjusting your search or filters'
                : 'Be the first to add a wine!'}
            </p>
            {isAuthenticated && canAddContent && (
              <Button
                onClick={() => navigate('/wines/new')}
                className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none"
                data-testid="empty-add-wine-btn"
              >
                Add First Wine
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Floating Compare Bar */}
      {selectedIds.length > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-stone-950/95 backdrop-blur-xl border border-[#d4af37]/40 shadow-2xl"
          data-testid="compare-bar"
        >
          <span className="text-xs uppercase tracking-widest text-stone-400">
            <span className="text-[#d4af37] font-semibold">{selectedIds.length}</span> selected
          </span>
          <Button
            size="sm"
            onClick={goToCompare}
            disabled={selectedIds.length < 2}
            className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs h-9 px-4"
            data-testid="go-compare-btn"
          >
            <GitCompare className="w-4 h-4 mr-2" />
            Compare
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearCompare}
            className="text-stone-500 hover:text-stone-300 rounded-none uppercase tracking-widest text-xs h-9"
            data-testid="clear-compare-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default Wines;
